/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  type ListContactsByFiltersRequest,
  useListContactsByFilters,
} from '@agent-assist/api-typescript-react-query-hooks';
import type {GridFilterModel} from '@mui/x-data-grid-premium';
import type {DateRangeValue} from '../../components/SuperTable/SuperDatePicker';
import {toColumns, toRows} from '../../services/super-table-service';
import {convertFilters} from '../../services/super-table-utils';

interface GetContactsListFilteredParams {
  contactImportId: string;
  filterModel?: GridFilterModel;
  dateRange?: DateRangeValue;
  limit: number;
  pageIndex: number;
  totalPages?: number;
  totalRecords?: number;
  totalFailedContacts?: number;
  totalCompletedContacts?: number;
}

// Helper function to format date without timezone conversion
const formatDatePreservingTimezone = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`;
};

export const useGetContactsListFiltered = () => {
  const {mutateAsync, isError, isLoading, isSuccess} = useListContactsByFilters();

  const get = async ({
    contactImportId,
    filterModel,
    dateRange,
    limit,
    pageIndex,
    totalPages,
    totalRecords,
    totalFailedContacts,
    totalCompletedContacts,
  }: GetContactsListFilteredParams) => {
    const filters = filterModel ? convertFilters(filterModel) : [];

    const [from, to] = dateRange || [];
    try {
      const params: ListContactsByFiltersRequest = {
        listContactsByFiltersRequestContent: {
          contactImportId,
          filters,
          from: from ? formatDatePreservingTimezone(from) : undefined,
          to: to ? formatDatePreservingTimezone(to) : undefined,
          limit: limit ?? 500,
          pageIndex,
          totalPages,
          totalRecords,
          totalFailedContacts,
          totalCompletedContacts,
        },
      };
      const {
        results,
        nextPage,
        totalPages: totalPagesFromQuery,
        totalRecords: totalRecordsFromQuery,
        totalFailedContacts: totalFailedContactsFromQuery,
        totalCompletedContacts: totalCompletedContactsFromQuery,
      } = await mutateAsync(params);
      if (!results) throw new Error('No results');
      const rows = toRows(results, {idField: 'contactId'});
      const columns = toColumns(results);
      return {
        rows,
        columns,
        nextPage,
        totalPages: totalPagesFromQuery,
        totalRecords: totalRecordsFromQuery,
        totalFailedContacts: totalFailedContactsFromQuery,
        totalCompletedContacts: totalCompletedContactsFromQuery,
      };
    } catch (e) {
      return Promise.reject(e);
    }
  };

  return {get, isError, isLoading, isSuccess};
};
